import React, { useContext, useState } from 'react';
import Loader2 from 'lucide-react/dist/esm/icons/loader-2';
import { toast } from 'sonner';

import { UserContext } from 'contexts/UserContext';
import { Alert, AlertDescription, AlertTitle } from 'shadcn/components/ui/Alert';
import { Switch } from 'shadcn/components/ui/Switch';
import { FacilityPickerV2 } from 'shared/components/FacilityPickerV2';
import { useFacilityFeatureFlagsQuery } from 'shared/hooks/warehouse/useFacilityFeatureFlagsQuery';
import { useSetFacilityFeatureFlagMutation } from 'shared/hooks/warehouse/useSetFacilityFeatureFlagMutation';

const SWITCH_CLASS_NAME =
    'h-5 w-9 border border-border shadow-none data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600 data-[state=unchecked]:bg-zinc-300 data-[state=unchecked]:border-zinc-400 [&_[data-slot=switch-thumb]]:size-[1.1rem] [&_[data-slot=switch-thumb]]:shadow-sm [&_[data-slot=switch-thumb]]:ring-1 [&_[data-slot=switch-thumb]]:ring-black/10';

/**
 * Admin UI for enabling/disabling whitelisted facility feature flags.
 */
export function FeatureFlagManagement() {
    const { user } = useContext(UserContext);
    const [facilityId, setFacilityId] = useState(user?.facilityID ?? null);

    const flagsQuery = useFacilityFeatureFlagsQuery({ facilityId });
    const setFlagMutation = useSetFacilityFeatureFlagMutation({
        onSuccess: () => {
            toast.success('Feature flag updated');
        },
        onError: (err) => {
            toast.error(err?.message ?? 'Failed to update feature flag');
        },
    });

    const flags = flagsQuery.data?.flags ?? [];
    const showLoading = Boolean(facilityId) && (flagsQuery.isLoading || (flagsQuery.isFetching && !flagsQuery.data));
    const showEmpty = !showLoading && flagsQuery.isSuccess && flags.length === 0;
    const pendingFeature =
        setFlagMutation.isPending && setFlagMutation.variables?.facilityId === facilityId
            ? setFlagMutation.variables.feature
            : null;

    const handleFacilityChange = (facility) => {
        setFacilityId(facility?.id ?? null);
    };

    const handleToggle = (feature, enabled) => {
        if (!facilityId || pendingFeature) {
            return;
        }
        setFlagMutation.mutate({ facilityId, feature, enabled });
    };

    return (
        <div className="mx-auto max-w-xl p-4 sm:p-6">
            <div className="mb-6">
                <h1 className="text-2xl font-semibold tracking-tight">Feature Flag Management</h1>
                <p className="mt-1 text-sm text-muted-foreground">Turn features on or off for a facility.</p>
            </div>

            <div className="mb-6 max-w-sm">
                <FacilityPickerV2 value={facilityId} onValueChange={handleFacilityChange} />
            </div>

            {!facilityId ? (
                <p className="text-sm text-muted-foreground">Select a facility to manage feature flags.</p>
            ) : null}

            {showLoading ? (
                <div
                    aria-label="Loading facility feature flags"
                    aria-live="polite"
                    className="flex items-center gap-2 py-8 text-muted-foreground"
                    role="status"
                >
                    <Loader2 aria-hidden="true" className="h-5 w-5 animate-spin" />
                    <span>Loading feature flags</span>
                </div>
            ) : null}

            {!showLoading && flagsQuery.isError ? (
                <Alert variant="destructive" role="alert">
                    <AlertTitle>Unable to load feature flags</AlertTitle>
                    <AlertDescription>{flagsQuery.error?.message ?? 'Something went wrong'}</AlertDescription>
                </Alert>
            ) : null}

            {showEmpty ? (
                <p className="text-sm text-muted-foreground">No managed feature flags for this facility.</p>
            ) : null}

            {!showLoading && flagsQuery.isSuccess && flags.length > 0 ? (
                <ul className="overflow-hidden rounded-lg border bg-card shadow-sm">
                    {flags.map(({ displayName, enabled, feature }) => {
                        const isUpdating = pendingFeature === feature;
                        let statusContent = enabled ? 'Enabled' : 'Disabled';
                        if (isUpdating) {
                            statusContent = (
                                <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin text-muted-foreground" />
                            );
                        }
                        return (
                            <li
                                key={feature}
                                className="flex items-center justify-between gap-4 border-b px-4 py-3.5 last:border-b-0"
                            >
                                <label
                                    className="min-w-0 flex-1 text-left text-sm font-medium leading-snug"
                                    htmlFor={`feature-flag-${feature}`}
                                >
                                    {displayName}
                                </label>
                                <div className="flex shrink-0 items-center gap-3">
                                    <span className="flex w-16 items-center justify-start text-xs text-muted-foreground">
                                        {statusContent}
                                    </span>
                                    <Switch
                                        id={`feature-flag-${feature}`}
                                        aria-label={displayName}
                                        checked={enabled}
                                        className={SWITCH_CLASS_NAME}
                                        onCheckedChange={(nextEnabled) => handleToggle(feature, nextEnabled)}
                                    />
                                </div>
                            </li>
                        );
                    })}
                </ul>
            ) : null}
        </div>
    );
}
